/**
 * /zh/creative-brief-template — 创意简报模板中文 SEO 页。与
 * CreativeBriefTemplate.tsx 同构，原生中文，hreflang 双向。
 */
import { MarketingShell } from './MarketingShell';

export function CreativeBriefTemplateZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/creative-brief-template"
      documentTitle="创意简报模板（一页纸）· Draftlize"
      documentDescription="一份可直接抄的创意简报模板——背景、目标、受众、单一核心信息、交付物、调性、硬性要求、时间线——并讲清怎么让它不与底下的决策脱节。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">模板 · 创意简报</span>
          <h1 className="serif" style={{ fontSize: 'clamp(38px, 4.8vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            一份创意简报模板，<br/>
            <em>工作无法从它身上漂走。</em>
          </h1>
          <p className="lede">
            你为一份<em>创意简报模板</em>（creative brief）而来——那张在任何人动手设计或写一个字之前，先让团队对齐的一页纸。它在下面；抄走。但简报有一个安静的失败：它是一个项目所建立其上的真相源，而一旦它底下的定位、受众或目标变了，简报就错了——那些已经从它做出来的 deck、campaign、文案，正朝着一个移动了的靶子漂移。Draftlize 给你同样的简报，连着它所依赖的决策。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">模板</span>
            <h2 className="serif">一页纸。<em>随处可抄。</em></h2>
          </div>
          <p>一份好的创意简报装得进一页，回答八件事。功夫在「单一核心信息」——如果你没法把它收成一句，简报还没准备好。</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: '背景 / 缘起', hint: '为什么是现在', body: '促成这项工作的情境——一次发布、一个问题、一种转变。够一个新人理解「它为什么存在」即可，不必更多。如果背景要写三段，这项目还没界定清楚。' },
            { k: '目标', hint: '成功是什么', body: '这项工作必须达成什么，用业务语言——认知、注册、认知转变。一个目标，而不是五个。它是之后评判每个创意决策的那个镜片。' },
            { k: '目标受众', hint: '具体是谁', body: '你在对话的那个具体的人——他的心态、他现在相信什么、什么能打动他。「所有人」做出的是谁都打动不了的东西。点名一个真实的人。' },
            { k: '单一核心信息', hint: '那一件事', body: '受众该带走的那一个想法，一句话说清。最难也最重要的一栏——如果有三个信息，就等于零个。其余一切都服务于它。' },
            { k: '交付物', hint: '做什么', body: '具体的产出和格式——一个落地页、三条社媒切片、一条 30 秒短片、规格与尺寸。含糊的交付物（「一些素材」）会在后面引发范围之争。' },
            { k: '调性与硬性要求', hint: '护栏', body: '它该有什么感觉，加上不可商量的——品牌、法务、必带的 logo 或主张、要避免的东西。这些护栏让作品不跑偏，又不至于对手艺指手画脚。' },
            { k: '时间线与预算', hint: '约束', body: '关键日期、评审里程碑、以及你能花多少。约束不是创意的敌人；一份开放无边的简报，才是那个永远上不了线的。' },
            { k: '它所依赖的决策', hint: '源头', body: '这份简报所执行的定位、策略或产品决策。在文档里这是大家「就是知道」的隐含上下文。它是决定「策略一动时简报还成不成立」的那一栏。' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.3 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.75, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '.04em', color: 'var(--ink-3)', textAlign: 'center', marginTop: 18 }}>
          创意简报是 <a href="/zh/product-brief-template" style={{ color: 'var(--amber)' }}>产品简报</a> 和 <a href="/zh/go-to-market-strategy-template" style={{ color: 'var(--amber)' }}>GTM 策略</a> 的表亲——同样的漂移问题，不同的面。
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">简报为什么<em>会漂移。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">它是工作活得比它久的一张快照</h3>
            <p>简报签批一次，然后数周的设计和文案建立在它上面。如果目标或受众中途变了——它们确实会——简报读起来仍是「已批准」，而它底下的假设已经动了，于是工作继续朝旧靶子行进。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">那个「为什么」住在别处</h3>
            <p>简报执行一个住在另一份文档里的定位或策略决策。简报把它复述成上下文，于是当那个决策变了，没有任何东西把两头连起来——策略动了，简报、以及从它简报出来的一切，悄悄各说各话。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">下游素材丢了线索</h3>
            <p>等到从一份简报里做出十个素材时，没人能追溯哪些还反映当前信息。对核心想法的一次修订，本该去标记每一个骑在旧想法上的素材——但在文件和文件夹里，没有任何东西会。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">同一份简报，<em>接上线。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">简报是连着决策的卡片</h3>
            <p>在 Draftlize 里，简报的目标和信息连到它所执行的定位决策，而不是复述它。简报引用它的源头，于是两者不会悄悄分叉成「简报说一套、策略说另一套」。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">策略一变，简报标 stale</h3>
            <p>重新定位，执行旧定位的那份简报自动标记 stale——连同从它简报出来的交付物。漂移在决策移动的那一刻浮现，而不是在一场「campaign 已和策略不符」的上线评审里。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的 agent 从当前信息出发简报</h3>
            <p>当 AI agent 经 MCP 起草文案或变体，它读到的是「活」的简报和它底下的决策——于是它生成的东西踩在当前信息上，而不是那个在「没人传播的重新定位」之前批准的版本。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          创意简报在第一天让所有人对齐。麻烦在于，工作在「它底下假设变了的那天」之后还在继续。
        </blockquote>
        <cite>留着这一页纸，把它的信息连到它所执行的决策。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '一份创意简报该包含什么？', a: '背景、一个目标、目标受众、一个单一核心信息、交付物、调性与硬性要求、时间线与预算。最强的简报装得进一页、能收成一个信息——如果有三个信息，简报就还没准备好。' },
            { q: '创意简报和产品简报有什么区别？', a: '创意简报让团队对齐于一件传播——一个 campaign、一个页面、一支广告；产品简报让团队对齐于「要做什么」。两者都是一页纸的对齐工具；一个指向信息与手艺，一个指向产品本身。' },
            { q: '创意简报由谁写？', a: '通常是市场、品牌负责人或客户/策略的人，与将要创作的人协作完成。和创意人一起写、而不是甩下来，才能趁不清楚的地方还便宜时把它们浮出来。' },
            { q: '创意简报该写多长？', a: '一页。把它装进一页的纪律，正是逼出单一核心信息、防止简报变成一个「说了所有、因而什么都没说」的倾倒场的关键。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">让被简报的工作不跑信息<em>——$5 免费起步。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>只为实际用量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            用上面的模板，或在 Draftlize 里把简报写成连着它所执行定位的卡片——这样策略一动，简报和从它简报出来的一切都标 stale，而不是朝一个变了的靶子漂移。
          </p>
          <a className="btn-primary" href="/">$5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
